package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.billing.BillingException
import com.evoapps.evoshift.domain.billing.OwnerPlan
import com.evoapps.evoshift.domain.billing.PlanOffer
import com.evoapps.evoshift.domain.billing.Plans
import com.evoapps.evoshift.domain.usecase.billing.GetPlanOffersUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObserveOwnerPlanUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObserveSeatsInUseUseCase
import com.evoapps.evoshift.domain.usecase.billing.PurchasePlanUseCase
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant

data class OverPlanState(
    /** The owner's plan; null until known. */
    val plan: OwnerPlan? = null,
    /** People in the owner's team across all workplaces, the owner not counted; null until known. */
    val seatsInUse: Int? = null,
    /** The offering's packages, asked for only once the team is past the plan. */
    val offers: List<PlanOffer> = emptyList(),
    /** "Not now": hidden until the app starts again (never in the last days before the renewal). */
    val dismissed: Boolean = false,
    val isPurchasing: Boolean = false,
    val message: UiText? = null,
    val error: UiText? = null
) {
    /** The seats of the owner's plan (bought or granted); null on the trial or without a plan. */
    val currentSeats: Int? get() = plan?.seats?.takeIf { plan.isPaid }

    val currentProductId: String? get() = plan?.storePlan?.entitlement?.productId

    /** The team has more people than the plan's seats; nobody was blocked on the way. */
    val isOverPlan: Boolean get() = currentSeats?.let { seats -> seatsInUse?.let { it > seats } } == true

    /** The next tier's offer (in the running plan's period); null while it fits or no tier is big enough. */
    val nextOffer: PlanOffer?
        get() = if (!isOverPlan) null else Plans.recommendedOffer(offers, seatsInUse ?: 0, currentSeats, currentProductId)

    /** The next tier's seats, known once the offers are; null before or when no tier is big enough. */
    val nextSeats: Int? get() = nextOffer?.seats

    /** When the plan renews (or ends); null when the store says nothing. */
    val renewsAt: Instant? get() = plan?.storePlan?.entitlement?.expiresAt ?: plan?.entitlement?.expiresAt

    /** The reminder before the renewal: within [Plans.OVER_PLAN_REMINDER_DAYS] of it. */
    fun remindsRenewal(now: Instant): Boolean =
        renewsAt?.let { it > now && it - now <= Plans.OVER_PLAN_REMINDER_DAYS.days } == true

    /** Whether the banner shows: past the plan, and not put off (the renewal reminder cannot be). */
    fun isShown(now: Instant): Boolean = isOverPlan && (!dismissed || remindsRenewal(now))
}

/**
 * SH-220: the owner's "your team outgrew the plan" banner. Growing past a seat tier never blocks
 * (CheckSeatsUseCase, acceptInvite); stores charge more only after the customer confirms, so
 * the next tier is proposed, and one tap starts the platform's own change: a Google Play
 * replacement, an App Store upgrade within the subscription group, or the web portal (all in
 * the billing repositories' purchase). It keeps reminding while the team is past the plan, and
 * names the renewal date in the last days before it. Owners only: it identifies the store SDK.
 */
class OverPlanViewModel(
    private val observeOwnerPlan: ObserveOwnerPlanUseCase,
    private val observeSeatsInUse: ObserveSeatsInUseUseCase,
    private val getPlanOffers: GetPlanOffersUseCase,
    private val purchasePlan: PurchasePlanUseCase,
    private val now: () -> Instant = { AppClock.now() }
) : ViewModel() {
    private val _state = MutableStateFlow(OverPlanState())
    val state: StateFlow<OverPlanState> = _state.asStateFlow()

    private var offersRequested = false

    init {
        viewModelScope.launch {
            observeOwnerPlan().catch { /* no plan known: no banner */ }.collect { plan ->
                _state.update { it.copy(plan = plan) }
                loadOffersWhenOver()
            }
        }
        viewModelScope.launch {
            observeSeatsInUse().catch { }.collect { used ->
                _state.update { it.copy(seatsInUse = used) }
                loadOffersWhenOver()
            }
        }
    }

    /** The offers are asked for only when there is something to propose. */
    private fun loadOffersWhenOver() {
        if (offersRequested || !_state.value.isOverPlan) return
        offersRequested = true
        viewModelScope.launch {
            getPlanOffers().onSuccess { offers -> _state.update { it.copy(offers = offers) } }
                .onFailure { offersRequested = false }
        }
    }

    fun isShown(): Boolean = _state.value.isShown(now())

    fun remindsRenewal(): Boolean = _state.value.remindsRenewal(now())

    /** One tap: the next tier through the platform's own change (the store asks to confirm). */
    fun upgrade() {
        val state = _state.value
        val offer = state.nextOffer ?: return
        if (state.isPurchasing) return
        _state.update { it.copy(isPurchasing = true, error = null, message = null) }
        viewModelScope.launch {
            val error = purchasePlan(offer).exceptionOrNull()
            if (error == null) AppTelemetry.log(AnalyticsEvent.Purchase(offer.productId))
            val reason = (error as? BillingException)?.reason
            _state.update {
                it.copy(
                    isPurchasing = false,
                    message = if (reason == BillingException.Reason.CHANGE_IN_PORTAL) error.toUiText() else null,
                    error = if (error != null && reason != BillingException.Reason.CANCELLED &&
                        reason != BillingException.Reason.CHANGE_IN_PORTAL
                    ) error.toUiText() else null
                )
            }
        }
    }

    /** "Not now": until the app starts again; the renewal reminder still shows. */
    fun dismiss() = _state.update { it.copy(dismissed = true, message = null, error = null) }
}
